/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useReducer, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import * as echarts from 'echarts';
import ReactEChartsCore from 'echarts-for-react';
import clsx from 'clsx';
import Icon from '@mdi/react';
import { mdiPlus, mdiMinus, mdiTrendingUp, mdiTrendingDown, mdiClose } from '@mdi/js';
import { hb } from 'relevant-shared/reportData/constants';
import metricSettings from 'relevant-shared/reportData/metricSettings';
import SystemData from '../../lib/systemData';
import { fmtNum } from '../../lib/numberUtils';
import geoJson from './world_countries_with_labels.json';
import { IconButton } from '../Button/IconButton';
import { MovableBox } from './movableBox';
import { Defaults } from '../Report/GeneralChartSettings';

const { CALCULATED_SUMS } = hb;

const NameAttrib = 'data-country-name';

echarts.registerMap('world', geoJson);

const zCss = (toAdd = 0) => `calc(var(--z-reportOverlay) + ${toAdd})`;

const getRelatedCountryBox = ({ event }) => {
	const elm = event?.relatedTarget?.closest(`[${NameAttrib}]`);
	return elm ? { elm, name: elm.getAttribute(NameAttrib) } : null;
};

// "Hack": we can't easily set event-listeners on the "react-draggable" div representing the info box for a hovered
// country. When the mouse leaves a country, we need to check if it's because it has entered that div instead. In
// that case cb() should NOT be called.
const waitCountryBoxCanBeClosed = (event, country, cb) => {
	if (!country || country.hadMouseMove) {
		return; // No country, or the inner div has already registered mouse movement
	}
	const { name, elm } = getRelatedCountryBox(event) || {};
	if (name !== country.name) {
		cb(); // No country box or the wrong one => close
		return;
	}
	elm.addEventListener('mouseleave', () => {
		if (!country.hadMouseMove) {
			cb(); // Still, no mouse movement registered over the "inner" div => this can be closed
		}
	});
};

interface BasicTooltipProps {
	children: React.ReactNode;
	isHover: boolean;
	placement?: 'top' | 'bottom' | 'left' | 'right';
}

// Cooltip.tsx didn't work easily because of the floating-ui stuff so just a simple tooltip component for now
export function BasicTooltip({
	children,
	isHover,
	placement = 'top',
}: BasicTooltipProps) {
	return (
		<div
			className={clsx(
				'relative flex bg-bg-interactive-light-bg-active_default rounded-lg border h-full w-full',
				isHover
					? 'border-borders-primary shadow-[0_4px_7px_2px_rgba(25,25,25,0.05)]'
					: 'border-borders-primary shadow-[inset_0_0_0_2px_rgba(120,120,120,0.35)]',
			)}
		>
			{children}

			{/* Arrow */}
			<div className={clsx(
				'absolute w-2.5 h-2.5 rotate-45 bg-bg-interactive-light-bg-active_default',
				{
					'left-1/2 -translate-x-1/2 -bottom-1': placement === 'top',
					'left-1/2 -translate-x-1/2 -top-1': placement === 'bottom',
					'top-1/2 -translate-y-1/2 -right-1': placement === 'left',
					'top-1/2 -translate-y-1/2 -left-1': placement === 'right',
				},
			)} />
		</div>
	);
}

export const ChoroplethSettingsKeys = ['geoOutlierThreshold', 'geoDontColorOutliers', 'maxChartsPerRow', 'geoZoom', 'geoCenterX', 'geoCenterY'] as const;

// Don't forget to update above array if you change this
interface ChoroplethSettings {
	geoOutlierThreshold: number,
	geoDontColorOutliers: boolean,
	maxChartsPerRow: number,
	geoZoom: number,
	geoCenterX: number,
	geoCenterY: number,
}

interface ChoroplethProps {
	calculator: any,
	metric: string,
	metricName: string,
	settings: ChoroplethSettings,
	style?: React.CSSProperties,
	className?: string;
	chartFormatted: never;
}

type Coords = [number, number];
type CountryLocation = {
	name: string;
	coords?: Coords;
	isHover: boolean;
	hadMouseMove?: boolean;
}
type CountryData = {
	country: string,
	total: number,
	byDim?: [number, string][], // value and label, sorted
	compTotal?: number,
	compByDim?: number[],
	changeAbs?: number,
	changeRel?: number,
	outlier?: boolean,
}

const HOVER_DELAY = 300; // ms, to wait before showing a tooltip

const prepareColors = ({ BACKGROUND_DEFAULT_COL, HOVER_BACKGROUND_DEFAULT_COL, COMP_BACKGROUND_DEFAULT_COL }) => {
	return _.zipObject(
		['bgColor', 'bgColorHover', 'bgColorComp'],
		[BACKGROUND_DEFAULT_COL, HOVER_BACKGROUND_DEFAULT_COL, COMP_BACKGROUND_DEFAULT_COL].map((colorVar) => {
			let color = colorVar;
			if (color.startsWith('--')) {
				color = getComputedStyle(document.body).getPropertyValue(color);
			}
			return color;
		}),
	);
};

const boxOffsetFn = ({ width, height }) => ({ x: -(width / 2), y: -height });

const setZoomTransitions = (containerElm, orgZoom, endZoom, duration = 500) => {
	if (!containerElm) {
		return;
	}
	const cRect = containerElm.getBoundingClientRect();
	const elms = [...containerElm.querySelectorAll(`[${NameAttrib}]`)]; // Find all country boxes
	elms.forEach((elm) => {
		const rect = elm.getBoundingClientRect();
		let { left: x, top: y } = rect;
		const offsets = boxOffsetFn(rect);
		// x + y => pixel position relative middle of canvas (0, 0 => middle) for the middle-bottom of box
		x += -offsets.x - (cRect.left + (cRect.width / 2));
		y += -offsets.y - (cRect.top + (cRect.height / 2));
		// How much distance from center should multiply
		const multiplier = endZoom / orgZoom;
		// let's convert x/y to pixel-position by:
		// 1) use the new "middle" position 2) put the offset back (to top-left corner), 3 add offset to container
		const newX = (x * multiplier) + offsets.x + cRect.width;
		const newY = (y * multiplier) + offsets.y + cRect.height;
		elm.style.transition = `transform ${duration}ms ease-in-out`;
		setTimeout(() => {
			elm.style.transition = '';
		}, duration);
		elm.style.transform = `translate(${newX}px, ${newY}px)`;
	});
};

const numOr = (num, defaultVal) => (typeof num === 'number' ? num : defaultVal);

export function Choropleth({
	calculator,
	metric,
	metricName,
	settings,
	style,
	className,
	chartFormatted,
}: ChoroplethProps) {
	const alreadyRelative = !!CALCULATED_SUMS[metric];
	const { useCompareTo: isComp, groupBy } = calculator.settings;
	const isMultiDim = calculator.properties().chartFormatLevels > 1;
	const { maxChartsPerRow: cols } = settings;

	// reshape the data so that it is easier to work with
	const dataPerCountry = useMemo(() => {
		const { obj, arr } = chartFormatted;
		const calcVal = (row, useComp) => calculator.calculateSumVal(row, metric, null, useComp);
		const perCountry = arr.reduce<{ [country: string]: { total: number, compTotal?: number }}>((acc, row) => {
			const data = row.totals || row.data;
			acc[row.label] = { total: calcVal(data, false) };
			if (isComp) {
				acc[row.label].compTotal = calcVal(data, true);
			}
			return acc;
		}, {});

		let overallThreshold: number;
		let compOverallThreshold: number;
		if (isComp) {
			const outlierThreshold = Math.min(Math.max(settings.geoOutlierThreshold ?? 0.05, 0), 1); // show countries making at least this much of the metric
			const sums = _.reduce(perCountry, (acc, countryData) => {
				acc.total += countryData.total;
				acc.compTotal += countryData.compTotal;
				return acc;
			}, { total: 0, compTotal: 0 });
			overallThreshold = sums.total * outlierThreshold;
			compOverallThreshold = sums.compTotal * outlierThreshold;
		}

		return _.mapValues(obj, (countryData, country): CountryData => {
			const byDim = isMultiDim ? _.map(countryData, (val, otherDimEntry) => [
				calcVal(val, false),
				otherDimEntry,
			]).sort((a, b) => b[0] - a[0]) : null;
			let compData: { changeAbs: number, changeRel: number, outlier: boolean } | undefined;
			if (isComp) {
				const { total, compTotal } = perCountry[country];
				const changeRel = compTotal ? ((total - compTotal) / compTotal) * 100 : 0;
				const changeAbs = total - compTotal;
				compData = {
					changeAbs,
					changeRel,
					outlier: alreadyRelative ? false : !(total > overallThreshold || compTotal > compOverallThreshold), // if already relative, the color range for changeAbs won't be crazy
				};
			}
			return {
				country,
				...perCountry[country],
				byDim,
				...compData,
			};
		});
	}, [calculator, metric, isComp, isMultiDim, settings.geoOutlierThreshold, alreadyRelative, chartFormatted]);

	// echarts bridge
	const chartRef = useRef<ReactEChartsCore>(null);

	// The number of chart columns the current and *ready* echarts component is made for
	const chartForCols = useRef<number>(-1);

	const [, forceRerender] = useReducer((x) => x + 1, 0); // Trick to force re-render after echart instance ready

	// chart state
	const [hoveredCountry, setHoveredCountry] = useState<CountryLocation>();

	const [selectedCountries, setSelectedCountries] = useState<CountryLocation[]>([]);
	const hoverTimeoutRef = useRef<number | null>(null);
	const hoveredNameRef = useRef<string | null>(null);
	const [zByCountry, setZByCountry] = useState({});

	const orgSettings = {
		zoom: settings.geoZoom ?? Defaults.geoZoom,
		center: [
			`${numOr(settings.geoCenterX, 0.5) * 100}%`,
			`${numOr(settings.geoCenterY, Defaults.geoCenterY) * 100}%`,
		],
	};
	const posSettings = useRef(orgSettings);
	const didMount = useRef(false);
	const useChange = (path, variable) => useEffect(() => {
		if (didMount.current) {
			const geo = _.cloneDeep(posSettings.current);
			_.set(geo, path, _.get(orgSettings, path));
			chartRef.current?.getEchartsInstance()?.setOption({ geo });
			forceRerender();
		}
	}, [path, variable]);
	useChange('zoom', settings.geoZoom);
	useChange('center.0', settings.geoCenterX);
	useChange('center.1', settings.geoCenterY);
	useEffect(() => { didMount.current = true; }, []);

	// move a country box to the top of the other ones (highest z-index)
	const bringToFront = (name) => {
		setZByCountry((prev) => {
			return { ...prev, [name]: Math.max(0, ...(_.values(prev) as number[])) + 1 };
		});
	};

	// save the coords for each country since we will look up when hovering
	const coordMap = useMemo(() => {
		const map: Record<string, Coords> = {};
		geoJson?.features.forEach((country) => {
			const { name, cp } = country.properties;
			if (name && cp) {
				map[name] = cp;
			}
		});
		return map;
	}, []);

	const compKey = alreadyRelative ? 'changeAbs' : 'changeRel';
	const dataKey = isComp ? compKey : 'total';
	const option = useMemo(() => {
		const { bgColor, bgColorComp } = prepareColors((SystemData as any).genericData.systemSettings);

		const { min, max } = _.isEmpty(dataPerCountry) ? { min: 0, max: 0 } : _.reduce(dataPerCountry, (acc, countryData) => {
			if (countryData.outlier) return acc;
			const value = countryData[dataKey];
			return {
				min: value < acc.min ? value : acc.min,
				max: value > acc.max ? value : acc.max,
			};
		}, { min: Infinity, max: -Infinity });

		// to match color intensities
		const absMax = Math.max(Math.abs(min), Math.abs(max));

		return {
			geo: {
				map: 'world',
				roam: true,
				aspectScale: 1,
				emphasis: {
					label: { show: false },
					itemStyle: {
						color: null,
						borderColor: '#333333',
					},
				},
				select: {
					label: { show: false },
					itemStyle: {
						color: null, // remove default
						borderColor: '#333333',
						borderWidth: 1,
					},
				},
				preserveAspect: true,
				...posSettings.current,
			},
			series: [
				{
					type: 'map',
					geoIndex: 0,
					selectedMode: 'multiple',
					// put in shape echarts expects
					data: _.map(dataPerCountry, (countryData) => ({
						name: countryData.country,
						value: (settings.geoDontColorOutliers && countryData.outlier) ? 0 : countryData[dataKey],
						selected: selectedCountries.some((c) => c.name === countryData.country),
					})),
				},
			],
			visualMap: [
				{
					type: 'continuous',
					calculable: true,
					itemWidth: cols === 1 ? 20 : 10,
					itemHeight: cols === 1 ? 140 : 70,
					formatter: (val) => `${fmtNum(val)}${isComp && dataKey === 'changeRel' ? '%' : ''}`, // add % since currently we use changeRel
					...(isComp ? { // if comparison, use different colors
						min: -absMax,
						max: absMax,
						inRange: { color: metricSettings.hb[metric]?.isNegativeIfIncrease ? [bgColorComp, '#FFFFFF', bgColor] : [bgColor, '#FFFFFF', bgColorComp] },
					} : {
						min,
						max,
						inRange: {
							color: bgColor,
							colorLightness: [0.95, 0.45],
						},
					}),
				},
			],
			tooltip: {
				show: false,
			},
		};
	}, [dataKey, dataPerCountry, isComp, metric, settings.geoDontColorOutliers, cols, selectedCountries]);

	const events = {
		mouseover: (params) => {
			if (!params.data) return;

			const { name } = params.data;
			hoveredNameRef.current = name;

			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}

			hoverTimeoutRef.current = window.setTimeout(() => {
				// Only show if still hovering same country
				if (hoveredNameRef.current === name && !_.find(selectedCountries, { name })) {
					bringToFront(name);
					setHoveredCountry({
						name,
						isHover: true,
					});
				}
			}, HOVER_DELAY);
		},
		mouseout: ({ event }) => {
			hoveredNameRef.current = null;

			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
				hoverTimeoutRef.current = null;
				waitCountryBoxCanBeClosed(event, hoveredCountry, () => (
					setHoveredCountry(null)
				));
			}
		},
		georoam: () => forceRerender(),
		selectchanged: () => {
			const curr = chartRef.current?.getEchartsInstance().getOption();
			const nowSelected = _.keys(_.pickBy(curr.series[0].selectedMap));
			if (nowSelected.includes(hoveredCountry?.name)) {
				setHoveredCountry(null);
			}
			const newSelected = _.difference(nowSelected, _.map(selectedCountries, 'name'));
			newSelected.forEach((name) => {
				bringToFront(name);
			});
			setSelectedCountries(nowSelected.map((name) => ({
				name,
				coords: _.find(selectedCountries, { name })?.coords,
				isHover: false,
			})));
		},
		finished: () => {
			const geo = chartRef.current?.getEchartsInstance()?.getOption()?.geo?.[0];
			if (geo) {
				posSettings.current = _.pick(geo, ['zoom', 'center']);
			}
		},
	};

	const chart = chartForCols.current === cols ? chartRef.current?.getEchartsInstance() : null;
	return (
		<div className="relative h-full w-full">
			<ReactEChartsCore
				echarts={echarts}
				key={`${cols}`}
				ref={chartRef}
				option={option}
				onEvents={events}
				onChartReady={() => {
					chartForCols.current = cols;
					forceRerender();
				}}
				style={style}
				className={className}
			/>
			{chart && (
				<ZoomButtons zoomFn={(mode: 'in' | 'out') => {
					const zoom = chart.getOption().geo[0].zoom * 2 ** (mode === 'in' ? 1 : -1);
					setZoomTransitions(chartRef.current?.ele?.parentNode, posSettings.current.zoom, zoom);
					chart.setOption({ geo: { zoom } });
					forceRerender();
				}}
				/>
			)}
			{chart && [
				...selectedCountries,
				// add hoveredCountry only if it is not selected already
				...(hoveredCountry && !selectedCountries.some((c) => c.name === hoveredCountry.name) ? [{ ...hoveredCountry, isHover: true }] : []),
			].map(({ name, coords, isHover }) => {
				const pixelCoords = chart.convertToPixel('geo', coords || coordMap[name]);
				const countryData: CountryData | Record<string, never> = dataPerCountry[name] || {};
				const compIncreased = countryData.changeAbs > 0;
				const compGoodChange = metricSettings.hb[metric]?.isNegativeIfIncrease ? !compIncreased : compIncreased;
				const onOver = () => {
					if (isHover && hoveredCountry) {
						hoveredCountry.hadMouseMove = true;
					}
				};
				const select = () => {
					bringToFront(name);
					if (isHover) {
						// When dragging the hovered box, make it selected instead
						chartRef.current?.getEchartsInstance().dispatchAction({
							type: 'select',
							seriesIndex: 0,
							name,
						});
						setHoveredCountry(null);
					}
				};
				const onNewCoords = (newCoords) => {
					const updated = (v) => ({ ...v, coords: chart.convertFromPixel('geo', newCoords) });
					const newSelected = selectedCountries.map((c) => (c.name === name ? updated(c) : c));
					setSelectedCountries(newSelected);
				};
				const onCloseButton = () => {
					if (isHover) {
						setHoveredCountry(null);
					}
					chartRef.current?.getEchartsInstance().dispatchAction({
						type: 'unselect',
						seriesIndex: 0,
						name,
					});
				};

				return (
					<MovableBox
						key={JSON.stringify([name, groupBy, isComp, cols])}
						maxInitHeight={200}
						pixelCoords={pixelCoords || [0, 0]}
						{...({ [NameAttrib]: name })}
						cancel=".non-draggable"
						onDragStart={select}
						onClick={select}
						onResizeStart={() => bringToFront(name)}
						onMouseLeave={() => isHover && setHoveredCountry(null)}
						{..._.mapValues(_.keyBy(['onMouseMove', 'onMouseEnter', 'onMouseOver']), () => onOver)}
						style={{ zIndex: zCss(zByCountry[name]) }}
						onNewCoords={onNewCoords}
						offsets={boxOffsetFn}
					>
						<BasicTooltip placement="top" isHover={isHover}>
							<div className="flex flex-col px-6 py-4 items-stretch gap-4 min-w-max h-full w-full">
								<IconButton
									size="s"
									className="absolute top-0 right-0 text-grey-500"
									icon={<Icon path={mdiClose} />}
									onTouchEnd={(e) => { // Necessary because of some react-rnd conflict that prevents onClick on mobile
										e.stopPropagation();
										onCloseButton();
									}}
									onClick={onCloseButton}
								/>
								<span className="font-medium">{name}</span>
								<div className="non-draggable cursor-auto flex-1 min-h-0 flex flex-col">
									{isComp && (
										<>
											<div className="flex flex-col items-start self-stretch gap-3 text-sm">
												<div className="flex items-center gap-2 self-stretch">
													<span className="text-text-tertiary font-semibold w-13">Before:</span>
													<span>{fmtNum(countryData.compTotal || 0)}</span>
												</div>
												<div className="flex items-center gap-2 self-stretch">
													<span className="text-text-tertiary font-semibold w-13">After:</span>
													<span>{fmtNum(countryData.total || 0)}</span>
												</div>
											</div>
											{!!countryData.changeAbs && (
												<div className={clsx('flex items-center gap-2 text-sm', compGoodChange ? 'text-bg-graphs-green-medium' : 'text-bg-graphs-red')}>
													<div className="flex items-center gap-1">
														<Icon path={compIncreased ? mdiTrendingUp : mdiTrendingDown} size="16px" />
														<span>{fmtNum(countryData.changeRel)}%</span>
													</div>
													<span>({compIncreased ? '+' : ''}{fmtNum(countryData.changeAbs)})</span>
												</div>
											)}
										</>
									)}
									{!isComp && isMultiDim && (
										<div className="flex flex-1 min-h-0 pr-3 flex-col items-start gap-3 self-stretch overflow-y-auto text-sm">
											{countryData.byDim?.map(([val, label], idx) => (
												<div key={idx} className="flex justify-between items-center gap-3 self-stretch">
													<span className="text-text-tertiary font-semibold">{label}</span>
													<span>{fmtNum(val)}</span>
												</div>
											))}
										</div>
									)}
									{!isComp && !isMultiDim && (
										<div className="flex items-center gap-2 self-stretch">
											<span className="text-text-tertiary font-semibold">{metricName}</span>
											<span>{fmtNum(countryData.total || 0)}</span>
										</div>
									)}
								</div>
							</div>
						</BasicTooltip>
					</MovableBox>
				);
			})}
		</div>
	);
}

function ZoomButtons({
	zoomFn,
}: {
	zoomFn: (mode: 'in' | 'out') => void,
}) {
	return (
		<div className="absolute bottom-0 right-0 flex flex-col gap-2">
			<IconButton
				size="s"
				icon={<Icon path={mdiPlus} />}
				onClick={() => zoomFn('in')}
			/>
			<IconButton
				size="s"
				icon={<Icon path={mdiMinus} />}
				onClick={() => zoomFn('out')}
			/>
		</div>
	);
}
